import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Spinner } from '@pijma/crypto'

import { sellApplicationClient } from '@api'
import { TFailureResponse } from '@api/types'
import { Dispatch, RootState } from '@stores'
import { ECurrency, isoToECurrency, TPayFormState } from '@stores/types/*'
import { HttpStatusCode } from 'axios'
import styled from 'styled-components'

import { EOperationErrorCause } from '../../../../api/types/EOperationErrorCause'
import { EPaymentProvider } from '../../../../stores/types/EPaymentProvider'
import { H4 } from '../../../common/header/Headings'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  margin-top: -80px;
`

const TitleWrapper = styled.div`
  margin-top: 16px;
  white-space: pre-wrap;
`

interface StateProps {
  payForm: TPayFormState
}

interface DispatchProps {
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
  TSuccessSellResponse?: boolean
}

type Props = StateProps & DispatchProps

const FindSellOffersPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()
  const applicationId = props.payForm?.application?.applicationUuid as string

  const defaultChecks = () => {
    if (!props.payForm.application?.applicationUuid) throw 'no applicationUuid'
    if (!props.payForm.paymentGateway) throw 'no gateway'
    if (!props.payForm.paymentGateway.paymentGatewayId)
      throw 'no paymentGatewayId'
    if (!props.payForm.amountFrom) throw 'no amountFrom'
    if (!props.payForm.amountTo) throw 'no amountTo'
  }

  const sell = () => {
    defaultChecks()
    if (!props.payForm.receiver) throw 'no receiver'
    if (!props.payForm.receiverBank) throw 'no receiverBank'
    if (!props.payForm.phone) throw 'no phone'

    sellApplicationClient
      .sell(
        props.payForm.application?.applicationUuid || '',
        props.payForm.amountFrom || '',
        props.payForm.amountTo || '',
        props.payForm.receiver,
        props.payForm.receiverBank.id,
        props.payForm.phone,
        props.payForm.paymentGateway?.paymentGatewayId || '',
      )
      .then((res) => {
        window.location.replace(res.redirectUrl)
      })
      .catch((reason) => catchReason(reason))
  }

  const sellImps = () => {
    defaultChecks()
    if (!props.payForm.impsAccount) throw 'no impsAccount'
    if (!props.payForm.impsAccountNum) throw 'no impsAccountNum'
    if (!props.payForm.ifsc) throw 'no ifsc'
    if (!props.payForm.senderFullName) throw 'no senderFullName'

    sellApplicationClient
      .sellImps(
        props.payForm.application?.applicationUuid || '',
        props.payForm.amountFrom || '',
        props.payForm.amountTo || '',
        props.payForm.impsAccount,
        props.payForm.impsAccountNum,
        props.payForm.ifsc,
        props.payForm.senderFullName,
        props.payForm.paymentGateway?.paymentGatewayId || '',
      )
      .then((res) => {
        window.location.replace(res.redirectUrl)
      })
      .catch((reason) => catchReason(reason))
  }

  const sellThbVnd = () => {
    defaultChecks()
    if (!props.payForm.receiverAccountHolder) throw 'no impsAccount'
    if (!props.payForm.receiverAccountNumber) throw 'no impsAccountNum'
    if (!props.payForm.receiverBank) throw 'no receiverBank'
    if (!props.payForm.senderFullName) throw 'no senderFullName'

    sellApplicationClient
      .sellThbVnd(
        props.payForm.application?.applicationUuid || '',
        props.payForm.amountFrom || '',
        props.payForm.amountTo || '',
        props.payForm.receiverAccountHolder,
        props.payForm.receiverAccountNumber,
        props.payForm.receiverBank.id,
        props.payForm.senderFullName,
        props.payForm.paymentGateway?.paymentGatewayId || '',
      )
      .then((res) => {
        window.location.replace(res.redirectUrl)
      })
      .catch((reason) => catchReason(reason))
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const catchReason = (reason: any) => {
    props.resetPaymentBlocking()
    const failureResponse = reason.response.data as TFailureResponse
    if (
      failureResponse.errorCode === EOperationErrorCause.NO_OFFERS_AVAILABLE
    ) {
      redirectTo(`../../application/${applicationId}/sell/offers-not-found`)
      return
    }
    if (failureResponse.errorCode === EOperationErrorCause.INSUFFICIENT_FUNDS) {
      redirectTo(`../../application/${applicationId}/sell/insufficient-funds`)
      return
    }
    console.log('something going wrong with payment', reason)
    if (reason.response.status === HttpStatusCode.Unauthorized) {
      redirectTo(`../../timeout`)
    } else {
      redirectTo(`../../failure`)
    }
  }

  useEffect(() => {
    if (props.payForm.paymentGateway?.provider === EPaymentProvider.SPAYZ) {
      const currencyTo = isoToECurrency(props.payForm.paymentGateway.currencyTo)
      switch (currencyTo) {
        case ECurrency.INR:
          sellImps()
          break
        case ECurrency.THB:
        case ECurrency.VND:
          sellThbVnd()
          break
        default:
          throw new Error(`Unknown currency ${currencyTo} for provider SPAYZ`)
      }
    } else {
      sell()
    }
    props.doPaymentBlocking()
  }, [])

  return (
    <FlexBoxCenteredLayout>
      <Container>
        <Spinner size={24} />

        <TitleWrapper>
          <H4>{intl.formatMessage({ id: 'findSellOffersPage.title' })}</H4>
        </TitleWrapper>
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FindSellOffersPage)
